// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useState, useEffect } from "react";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CircularProgress from "@mui/material/CircularProgress";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftSnackbar from "components/SoftSnackbar";

// Dashboard layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Services
import { getAllContacts, createContact, updateContact, deleteContact } from "services/contactService";

function Contacts() {
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [openContactDialog, setOpenContactDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactFormData, setContactFormData] = useState({
    Vorname: "",
    Nachname: "",
    email: "",
    phone: "",
    department: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    color: "info",
  });

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const data = await getAllContacts();
      setContacts(data);
    } catch (error) {
      console.error("Error fetching contacts:", error);
      setSnackbar({
        open: true,
        message: "Fehler beim Laden der Kontakte",
        color: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenContactDialog = (contact = null) => {
    if (contact) {
      // Edit mode - fill form with contact data
      setContactFormData({
        Vorname: contact.Vorname || "",
        Nachname: contact.Nachname || "",
        email: contact.email || "",
        phone: contact.phone || "",
        department: contact.address || "", // Address field is used for department
      });
      setSelectedContactId(contact.id);
    } else {
      // Add mode - reset form
      setContactFormData({
        Vorname: "",
        Nachname: "",
        email: "",
        phone: "",
        department: "",
      });
      setSelectedContactId(null);
    }
    setOpenContactDialog(true);
  };

  const handleCloseContactDialog = () => {
    setOpenContactDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContactFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSaveContact = async () => {
    // Validation
    if (!contactFormData.Vorname || !contactFormData.Nachname || !contactFormData.email) {
      setSnackbar({
        open: true,
        message: "Name und E-Mail sind erforderlich",
        color: "warning",
      });
      return;
    }

    try {
      setLoading(true);
      if (selectedContactId) {
        // Update existing contact
        await updateContact(selectedContactId, contactFormData);
        setSnackbar({
          open: true,
          message: "Kontakt erfolgreich aktualisiert",
          color: "success",
        });
      } else {
        // Create new contact
        await createContact(contactFormData);
        setSnackbar({
          open: true,
          message: "Kontakt erfolgreich erstellt",
          color: "success",
        });
      }
      handleCloseContactDialog();
      fetchContacts();
    } catch (error) {
      console.error("Error saving contact:", error);
      setSnackbar({
        open: true,
        message: "Fehler beim Speichern des Kontakts",
        color: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteDialog = (contactId) => {
    setSelectedContactId(contactId);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedContactId(null);
  };

  const handleDeleteContact = async () => {
    try {
      setLoading(true);
      await deleteContact(selectedContactId);
      setSnackbar({
        open: true,
        message: "Kontakt erfolgreich gelöscht",
        color: "success",
      });
      handleCloseDeleteDialog();
      fetchContacts();
    } catch (error) {
      console.error("Error deleting contact:", error);
      setSnackbar({
        open: true,
        message: "Fehler beim Löschen des Kontakts",
        color: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Prepare data for DataTable component
  const columns = [
    { Header: "Name", accessor: "name", width: "25%" },
    { Header: "E-Mail", accessor: "email", width: "25%" },
    { Header: "Telefon", accessor: "phone", width: "20%" },
    { Header: "Abteilung", accessor: "department", width: "20%" },
    { Header: "Aktionen", accessor: "actions", width: "10%", align: "center" },
  ];

  const rows = contacts.map((contact) => ({
    name: `${contact.Vorname} ${contact.Nachname}`,
    email: contact.email,
    phone: contact.phone || "-",
    department: contact.address || "-", // Address field is used for department
    actions: (
      <SoftBox display="flex" justifyContent="center">
        <IconButton 
          size="small" 
          color="info" 
          onClick={() => handleOpenContactDialog(contact)}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton 
          size="small" 
          color="error" 
          onClick={() => handleOpenDeleteDialog(contact.id)}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </SoftBox>
    ),
  }));

  const tableData = {
    columns,
    rows,
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={3}>
        <Card>
          <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" p={3}>
            <SoftBox>
              <SoftTypography variant="h5" fontWeight="medium">
                Kontakte
              </SoftTypography>
              <SoftTypography variant="body2" fontWeight="regular" color="text">
                Verwalten Sie hier Ihre Mitarbeiter- und Abteilungskontakte
              </SoftTypography>
            </SoftBox>
            <SoftButton 
              variant="gradient" 
              color="info" 
              onClick={() => handleOpenContactDialog()}
            >
              Kontakt hinzufügen
            </SoftButton>
          </SoftBox>
          {loading && contacts.length === 0 ? (
            <SoftBox display="flex" justifyContent="center" p={3}>
              <CircularProgress color="info" />
            </SoftBox>
          ) : (
            <DataTable
              table={tableData}
              entriesPerPage={{
                defaultValue: 10,
                entries: [5, 10, 15, 20],
              }}
              canSearch
            />
          )}
        </Card>
      </SoftBox>

      {/* Dialog für das Hinzufügen/Bearbeiten von Kontakten */}
      <Dialog 
        open={openContactDialog} 
        onClose={handleCloseContactDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {selectedContactId ? "Kontakt bearbeiten" : "Neuen Kontakt anlegen"}
        </DialogTitle>
        <DialogContent>
          <SoftBox component="form" role="form" p={2}>
            <SoftBox mb={2}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Vorname*
                </SoftTypography>
              </SoftBox>
              <SoftInput
                name="Vorname"
                placeholder="Max"
                value={contactFormData.Vorname}
                onChange={handleInputChange}
                fullWidth
              />
            </SoftBox>
            
            <SoftBox mb={2}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Nachname*
                </SoftTypography>
              </SoftBox>
              <SoftInput
                name="Nachname"
                placeholder="Mustermann"
                value={contactFormData.Nachname}
                onChange={handleInputChange}
                fullWidth
              />
            </SoftBox>
            
            <SoftBox mb={2}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  E-Mail*
                </SoftTypography>
              </SoftBox>
              <SoftInput
                name="email"
                placeholder="max.mustermann@example.com"
                type="email"
                value={contactFormData.email}
                onChange={handleInputChange}
                fullWidth
              />
            </SoftBox>

            <SoftBox mb={2}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Telefon
                </SoftTypography>
              </SoftBox>
              <SoftInput
                name="phone"
                placeholder="+49 123 45678"
                value={contactFormData.phone}
                onChange={handleInputChange}
                fullWidth
              />
            </SoftBox>

            <SoftBox mb={2}>
              <SoftBox mb={1} ml={0.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Abteilung
                </SoftTypography>
              </SoftBox>
              <SoftInput
                name="department"
                placeholder="IT-Abteilung"
                value={contactFormData.department}
                onChange={handleInputChange}
                fullWidth
              />
            </SoftBox>
          </SoftBox>
        </DialogContent>
        <DialogActions>
          <SoftButton onClick={handleCloseContactDialog} color="secondary">
            Abbrechen
          </SoftButton>
          <SoftButton onClick={handleSaveContact} color="info">
            {loading ? "Wird gespeichert..." : "Speichern"}
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* Dialog für das Löschen von Kontakten */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Kontakt löschen</DialogTitle>
        <DialogContent>
          <SoftTypography variant="body1">
            Möchten Sie diesen Kontakt wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
          </SoftTypography>
        </DialogContent>
        <DialogActions>
          <SoftButton onClick={handleCloseDeleteDialog} color="secondary">
            Abbrechen
          </SoftButton>
          <SoftButton onClick={handleDeleteContact} color="error">
            {loading ? "Wird gelöscht..." : "Löschen"}
          </SoftButton>
        </DialogActions>
      </Dialog>

      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Hinweis"
        content={snackbar.message}
        open={snackbar.open}
        onClose={handleCloseSnackbar}
        close={handleCloseSnackbar}
        bgWhite
      />
      <Footer />
    </DashboardLayout>
  );
}

export default Contacts;